import {
  useMutation,
  useQuery,
  useQueryClient,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
} from "@tanstack/react-query";
import { apiClient } from "../services/api";

// Conserva el `data` de la respuesta en el Error; sin esto solo sobrevive el
// mensaje y se pierde informacion como retry_after_seconds.
const toError = (error: any, fallback: string) => {
  const wrapped = new Error(
    error?.response?.data?.message || error?.message || fallback,
  );
  (wrapped as any).data = error?.response?.data?.data ?? null;
  return wrapped;
};

export const useDataFetch = () => {
  const queryClient = useQueryClient();

  const authHeaders = {
    "Content-Type": "application/json",
    Authorization:   `${localStorage.getItem("token") || ""}`, 
    Accept: "application/json",
  };

  const useGetData = (
    queryKey: QueryKey,
    url: string,
    options: Omit<UseQueryOptions<any, Error, any, QueryKey>, 'queryKey' | 'queryFn'> = {},
    params: Record<string, any> = {},
    headers: Record<string, string> = {}
  ) => {
    return useQuery({
      queryKey,
      queryFn: async () => {
        try {
          const response = await apiClient.get(url, {
            params,
            headers: { ...headers, ...authHeaders },
          });
          if (response.status === 500) {
            throw new Error("Failed to fetch data");
          }
          return response?.data;
        } catch (error: any) {
          throw toError(error, "Error de conexión con el servidor");
        }
      },
      retry: 2,
      enabled: true,
      refetchOnWindowFocus: false,
      ...options,
    });
  };

  const usePostData = (
    url: string,
    headers: Record<string, string> = {},
    options: Omit<UseMutationOptions<any, Error, any>, 'mutationFn'> = {}
  ) => {
    return useMutation({
      mutationFn: async (body: any) => {
        try {
          const response = await apiClient.post(url, body, {
            headers: { ...headers, ...authHeaders },
          });

          if (response.status === 500) {
            throw new Error("Failed to fetch data");
          } else if (response.status === 400) {
            throw new Error(response.data.message);
          }

          return response?.data;
        } catch (error: any) {
          throw toError(error, "Error al procesar la solicitud");
        }
      },
      ...options,
    });
  };

  const usePatchData = (
    url: string,
    headers: Record<string, string> = {},
    options: Omit<UseMutationOptions<any, Error, any>, 'mutationFn'> = {}
  ) => {
    return useMutation({
      mutationFn: async (body: any) => {
        try {
          if (body.id) {
            url = `${url}/${body.id}`;
            delete body.id;
          }
          const response = await apiClient.patch(`${url}`, body, {
            headers: { ...headers, ...authHeaders },
          });

          if (response.status === 500) {
            throw new Error("Failed to fetch data");
          } else if (response.status === 400) {
            throw new Error(response.data.message);
          }

          return response?.data;
        } catch (error: any) {
          throw toError(error, "Error al actualizar el registro");
        }
      },
      ...options,
    });
  };

  const invalidateQuery = (queryKey: QueryKey) => {
    queryClient.invalidateQueries({ queryKey });
  };

  return {
    getData: useGetData,
    postData: usePostData,
    patchData : usePatchData,
    invalidateQuery,
  };
};
